import React, { useState } from 'react';
import { Button } from './ui/button';
import { HitoIcon, KpiIconIngresos, KpiIconGastos, KpiIconRunway, KpiIconPersonas, KpiIconRonda, ValoracionIcon, EventoQ4Icon } from './IconsReporte';

const INDICADORES_LABELS = {
  startups_vivas: 'Startups vivas',
  exits: 'Exits',
  fallidas: 'Fallidas',
  inicio_actividad: 'Inicio de actividad',
  startups_recibidas_q3: 'Nº startups recibidas Q3 / Total analizadas',
  inversiones_q3: 'Nº inversiones realizadas Q3',
  n_socios: 'Nº de socios inversores',
  reuniones_analisis_q3: 'Nº de reuniones análisis Q3',
  total_invertido_q3_eur: 'Total € invertidos Q3',
  valor_teorico_cartera_eur: 'Valor teórico cartera (m €)',
  total_compromisos_desembolso_eur: 'Total compromisos desembolso €',
  comites_seleccion_q3: 'Nº Comités de Selección Q3',
  revalorizacion_cartera_eur: 'Revalorización cartera (m €)',
  comites_inversion_q3: 'Nº Comités de Inversión Q3',
  revalorizacion_cartera_pct: 'Revalorización cartera %',
  startups_aprobadas_q3: 'Startups aprobadas Q3',
  startups_invertidas: 'Startups invertidas'
};

/**
 * Vista del informe con aspecto idéntico a la web de ejemplo (pinama-reporting.vercel.app).
 * Recibe el reporte, contenido genérico y startups para renderizar el informe completo.
 */
export const SECTION_IDS = {
  introduccion: 'introduccion',
  resumen_ejecutivo: 'resumen-ejecutivo',
  actividad_inversion: 'actividad-inversion',
  eventos: 'eventos',
  indicadores: 'indicadores',
  participadas: 'participadas',
  calendario: 'calendario',
  anexos: 'anexos'
};

const VALORACION_TO_FILTER = {
  'Exit': 'exit',
  'Fallida': 'fallida',
  'Mejor de lo esperado': 'mejor',
  'En línea con lo esperado': 'en-linea'
};

function ParticipadaCard({ s, isOpen, onToggle, visible }) {
  const k = s.datos?.kpis || {};
  const milestones = s.datos?.milestones || [];
  const hitos = Array.isArray(milestones) ? milestones.map((m) => (m && (m.descripcion || m.texto || m.text)) ? (m.descripcion || m.texto || m.text) : String(m)) : [];
  const valoracion = k.valoracion || 'En línea con lo esperado';
  const filterClass = VALORACION_TO_FILTER[valoracion] || 'en-linea';
  const filter = filterClass === 'en-linea' ? 'valoracion-en-linea' : `valoracion-${filterClass}`;
  const hasKpis = k.ingresos != null || k.gastos != null || k.runway != null || k.empleados != null || k.personas != null || k.ronda != null || k.progresoRonda != null;
  const kpisForDisplay = hasKpis ? {
    ingresos: k.ingresos,
    gastos: k.gastos,
    runway: k.runway,
    personas: k.personas ?? k.empleados,
    ronda: k.ronda ?? (k.progresoRonda || (k.rondaAbierta ? 'Sí' : 'No'))
  } : null;
  const summary = k.summary ?? s.descripcion ?? '';
  const web = k.web ?? s.website ?? '';
  const logoUrl = k.logo_url || k.logoUrl || s.logo_url || '';

  return (
    <article className={`participada-card ${filter} ${isOpen ? 'is-open' : ''}`} data-filter={filterClass} style={visible === false ? { display: 'none' } : undefined}>
      <header className="participada-header">
        <div className="participada-logo">
          {logoUrl ? <img src={logoUrl} alt="" /> : null}
        </div>
        <div className="participada-header-text">
          <div className="participada-meta">
            {k.incorporation && <span className="participada-incorporation">Incorporación a cartera {k.incorporation}</span>}
            <span className="valoracion-badge">{valoracion}</span>
          </div>
          <h3 className="participada-name">{s.nombre}</h3>
          {valoracion === 'Exit' && k.exitValue && <div className="participada-exit-badge">Exit — {k.exitValue}</div>}
          <div className="participada-figures">
            {k.totalInvertido && <span>Total invertido: <strong>{k.totalInvertido}</strong></span>}
            {k.pctPortfolio && <span>% Portfolio: <strong>{k.pctPortfolio}</strong></span>}
            {k.pctEquity && <span>% Equity: <strong>{k.pctEquity}</strong></span>}
          </div>
          <p className="participada-arcangel">Arcángel: {s.arcangel || '—'}</p>
          {summary && <p className="participada-summary">{summary}</p>}
          {web && <a href={web} target="_blank" rel="noopener noreferrer" className="participada-web">Página web →</a>}
        </div>
      </header>
      <div className="participada-body">
        {hitos.length > 0 && (
          <div className="participada-hitos">
            <p className="participada-block-title">Hitos destacados del trimestre</p>
            <div className="hitos-list">
              {hitos.map((h, i) => (
                <div key={i} className="hito-item">
                  <span className="hito-icon" aria-hidden="true"><HitoIcon /></span>
                  <span className="hito-text">{h}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {kpisForDisplay && Object.values(kpisForDisplay).some((v) => v != null && v !== '') && (
          <div className="participada-kpis">
            <p className="participada-block-title">KPIs</p>
            <div className="kpis-grid">
              {kpisForDisplay.ingresos != null && kpisForDisplay.ingresos !== '' && (
                <div className="kpi-item"><span className="kpi-icon" aria-hidden="true"><KpiIconIngresos /></span><div className="kpi-item-content"><span className="kpi-item-label">Ingresos 3T</span><span className="kpi-item-value">{String(kpisForDisplay.ingresos)}</span></div></div>
              )}
              {kpisForDisplay.gastos != null && kpisForDisplay.gastos !== '' && (
                <div className="kpi-item"><span className="kpi-icon" aria-hidden="true"><KpiIconGastos /></span><div className="kpi-item-content"><span className="kpi-item-label">Gastos 3T</span><span className="kpi-item-value">{String(kpisForDisplay.gastos)}</span></div></div>
              )}
              {kpisForDisplay.runway != null && kpisForDisplay.runway !== '' && (
                <div className="kpi-item"><span className="kpi-icon" aria-hidden="true"><KpiIconRunway /></span><div className="kpi-item-content"><span className="kpi-item-label">Runway</span><span className="kpi-item-value">{String(kpisForDisplay.runway)}</span></div></div>
              )}
              {kpisForDisplay.personas != null && kpisForDisplay.personas !== '' && (
                <div className="kpi-item"><span className="kpi-icon" aria-hidden="true"><KpiIconPersonas /></span><div className="kpi-item-content"><span className="kpi-item-label">Personas</span><span className="kpi-item-value">{String(kpisForDisplay.personas)}</span></div></div>
              )}
              {kpisForDisplay.ronda != null && kpisForDisplay.ronda !== '' && (
                <div className="kpi-item"><span className="kpi-icon" aria-hidden="true"><KpiIconRonda /></span><div className="kpi-item-content"><span className="kpi-item-label">Ronda</span><span className="kpi-item-value">{String(kpisForDisplay.ronda)}</span></div></div>
              )}
            </div>
          </div>
        )}
        {!kpisForDisplay && s.datos?.kpis && (s.datos.kpis.kpisText || Object.keys(s.datos.kpis).some((key) => !['incorporation', 'totalInvertido', 'pctPortfolio', 'pctEquity', 'summary', 'web', 'valoracion', 'exitValue'].includes(key) && s.datos.kpis[key] != null)) && (
          <div className="participada-kpis participada-kpis-text">
            <p className="participada-block-title">KPIs</p>
            <p className="kpis-plain">{s.datos.kpis.kpisText || Object.entries(s.datos.kpis).filter(([key, v]) => !['incorporation', 'totalInvertido', 'pctPortfolio', 'pctEquity', 'summary', 'web', 'valoracion', 'exitValue'].includes(key) && v != null && v !== '').map(([k2, v]) => `${k2}: ${v}`).join(' · ')}</p>
          </div>
        )}
        <div className="participada-valoracion-wrap">
          <span className="valoracion-icon" aria-hidden="true"><ValoracionIcon /></span>
          <p className="participada-valoracion-label"><strong>Valoración cualitativa del equipo PC23:</strong> {valoracion}</p>
        </div>
      </div>
      <button type="button" className="participada-toggle" aria-expanded={isOpen} onClick={onToggle}>
        {isOpen ? 'Ver menos' : 'Ver más'}
      </button>
    </article>
  );
}

export default function VistaInforme({ reporte, contenidoGenerico, startupsReporte, onCerrar, showHeader = true, sectionIds = null }) {
  const c = contenidoGenerico || {};
  const trimestre = (reporte?.trimestre || '').replace(/\s/g, '');
  const anio = reporte?.anio || reporte?.año || '';
  const ids = sectionIds || SECTION_IDS;
  const [openParticipadaId, setOpenParticipadaId] = useState(null);
  const [filterParticipadas, setFilterParticipadas] = useState('all');

  const isPublicPage = !showHeader;

  return (
    <div className={showHeader ? 'fixed inset-0 z-50 bg-background overflow-auto' : 'w-full'}>
      {showHeader && (
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b flex items-center justify-between px-4 py-2">
          <span className="text-sm text-muted-foreground">Vista previa del informe</span>
          <Button variant="outline" size="sm" onClick={onCerrar}>
            Cerrar vista
          </Button>
        </div>
      )}

      {isPublicPage ? (
        <div className="container">
          {/* Introducción */}
          {c.introduccion && (
            <section id={ids.introduccion} className="section intro">
              <div className="narrow">
                <p className="eyebrow">Introducción</p>
                <h2 className="section-title">Introducción</h2>
                <div className="prose" style={{ whiteSpace: 'pre-line' }}>{c.introduccion}</div>
              </div>
            </section>
          )}

          {/* Resumen ejecutivo */}
          {(c.resumen_cards?.length > 0 || c.resumen_ejecutivo) && (
            <section id={ids.resumen_ejecutivo} className="section">
              <div className="container">
                <p className="eyebrow">Resumen Ejecutivo</p>
                <h2 className="section-title">Resumen Ejecutivo</h2>
                {c.section_lead_resumen && <p className="section-lead">{c.section_lead_resumen}</p>}
                <div className="resumen-grid">
                  {c.resumen_cards && c.resumen_cards.length > 0
                    ? c.resumen_cards.map((card, i) => (
                        <div key={i} className={`resumen-card ${card.type === 'highlight' ? 'highlight' : ''} ${card.type === 'warning' ? 'warning' : ''}`}>
                          {card.type === 'highlight' && <span className="resumen-icon">✓</span>}
                          {card.type === 'warning' && <span className="resumen-icon">!</span>}
                          {card.title && <h3>{card.title}</h3>}
                          {card.content && <p dangerouslySetInnerHTML={{ __html: card.content.replace(/\n/g, '<br />') }} />}
                          {card.items && card.items.length > 0 && (
                            <ul>
                              {card.items.filter(Boolean).map((item, j) => (
                                <li key={j} dangerouslySetInnerHTML={{ __html: item.replace(/\n/g, '<br />') }} />
                              ))}
                            </ul>
                          )}
                        </div>
                      ))
                    : (
                        <div className="resumen-card">
                          <div className="prose" style={{ whiteSpace: 'pre-line' }}>{c.resumen_ejecutivo}</div>
                        </div>
                      )}
                </div>
                {c.resumen_footer && <p className="resumen-footer">{c.resumen_footer}</p>}
                {c.signature && <p className="signature">{c.signature}</p>}
              </div>
            </section>
          )}

          {/* Comités */}
          {c.comites && c.comites.length > 0 && (
            <section id={ids.actividad_inversion} className="section">
              <div className="container">
                <p className="eyebrow">Actividad de inversión</p>
                <h2 className="section-title">Actividad de análisis e inversión</h2>
                <p className="section-lead">Comités de Selección e Inversión celebrados en el trimestre.</p>
                <div className="comites-grid">
                  {c.comites.map((comite, i) => (
                    <div key={i} className="comite-card">
                      <span className="comite-badge">{comite.tipo}</span>
                      <p><strong>{comite.nombre}</strong></p>
                      <p>{comite.fecha}</p>
                      {comite.descripcion && <p>{comite.descripcion}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Eventos */}
          {c.eventos && c.eventos.length > 0 && (
            <section id={ids.eventos} className="section">
              <div className="container">
                <p className="eyebrow">Ecosistema</p>
                <h2 className="section-title">Presencia en eventos del ecosistema</h2>
                <p className="section-lead">Eventos del ecosistema inversor-emprendedor en los que Pinama ha tenido presencia.</p>
                <div className="eventos-cards">
                  {c.eventos.map((ev, i) => (
                    <div key={i} className="evento-card">
                      {ev.imagen ? (
                        <div className="evento-card-img-wrap">
                          <img src={ev.imagen} alt="" />
                        </div>
                      ) : (
                        <div className="evento-card-img-wrap" />
                      )}
                      <div className="evento-card-text">
                        <strong>{ev.nombre}</strong>
                        <br />
                        <span>{ev.fecha}{ev.descripcion ? ` · ${ev.descripcion}` : ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Indicadores */}
          {c.indicadores && Object.keys(c.indicadores).length > 0 && (
            <section id={ids.indicadores} className="section hitos">
              <div className="container">
                <p className="eyebrow">Indicadores</p>
                <h2 className="section-title">Principales hitos e indicadores – {trimestre} {anio}</h2>
                <p className="section-lead">Métricas clave del vehículo a cierre del trimestre.</p>
                <div className="kpi-grid">
                  {Object.entries(c.indicadores).map(([key, value]) => {
                    if (value === '' || value === null || value === undefined) return null;
                    return (
                      <div key={key} className="kpi-card">
                        <span className="kpi-value">{String(value)}</span>
                        <span className="kpi-label">{INDICADORES_LABELS[key] || key}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* Participadas */}
          {startupsReporte && startupsReporte.length > 0 && (
            <section id={ids.participadas} className="section">
              <div className="container">
                <p className="eyebrow">Cartera</p>
                <h2 className="section-title">Participadas</h2>
                <p className="section-lead">Detalle de cada participada con KPIs, valoración y hitos del trimestre.</p>
                <div className="participadas-filters">
                  <span className="filter-label">Filtrar:</span>
                  {['all', 'en-linea', 'mejor', 'exit', 'fallida'].map((f) => (
                    <button
                      key={f}
                      type="button"
                      className={`filter-btn ${filterParticipadas === f ? 'active' : ''}`}
                      onClick={() => setFilterParticipadas(f)}
                    >
                      {f === 'all' ? 'Todas' : f === 'en-linea' ? 'En línea' : f === 'mejor' ? 'Mejor de lo esperado' : f === 'exit' ? 'Exit' : 'Fallida'}
                    </button>
                  ))}
                </div>
                <div className="participadas-list">
                  {startupsReporte.map((s) => {
                    const k = s.datos?.kpis || {};
                    const val = k.valoracion || 'En línea con lo esperado';
                    const cardFilter = VALORACION_TO_FILTER[val] || 'en-linea';
                    const visible = filterParticipadas === 'all' || cardFilter === filterParticipadas;
                    return (
                      <ParticipadaCard
                        key={s.id}
                        s={s}
                        isOpen={openParticipadaId === s.id}
                        onToggle={() => setOpenParticipadaId((prev) => (prev === s.id ? null : s.id))}
                        visible={visible}
                      />
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* Calendario */}
          {c.calendario && c.calendario.length > 0 && (
            <section id={ids.calendario} className="section">
              <div className="container">
                <p className="eyebrow">Calendario</p>
                <h2 className="section-title">Eventos programados en el Q4 – {anio}</h2>
                <ul className="eventos-q4-list">
                  {c.calendario.map((item, i) => (
                    <li key={i}>
                      <span className="evento-q4-icon" aria-hidden="true"><EventoQ4Icon /></span>
                      <span><strong>{item.titulo}</strong>: {item.fecha}. {item.descripcion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Anexos */}
          <section id={ids.anexos} className="section">
            <div className="container">
              <p className="eyebrow">Anexos</p>
              <h2 className="section-title">Anexos</h2>

              {c.anexo_i && (
                <div className="anexo">
                  <h3 className="anexo-title">Anexo I: Resumen del vehículo</h3>
                  <div className="anexo-body">{c.anexo_i}</div>
                </div>
              )}

              {(c.anexo_ii || c.calendario_comites) && (
                <div className="anexo">
                  <h3 className="anexo-title">Anexo II: Calendario de Comités de Selección e Inversión {c.calendario_comites?.anio || anio}</h3>
                  {c.calendario_comites && (() => {
                    const anioCal = c.calendario_comites.anio || parseInt(anio, 10) || new Date().getFullYear();
                    const seleccion = c.calendario_comites.seleccion || [];
                    const inversion = c.calendario_comites.inversion || [];
                    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                    return (
                      <div className="calendario-web-wrap">
                        <div className="calendario-leyenda">
                          <span className="cal-leyenda-item cal-seleccion">Comité Selección</span>
                          <span className="cal-leyenda-item cal-inversion">Comité Inversión</span>
                        </div>
                        <div className="calendario-grid">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                            const daysInMonth = new Date(anioCal, m, 0).getDate();
                            const selDays = seleccion.filter((x) => x.month === m).map((x) => x.day);
                            const invDays = inversion.filter((x) => x.month === m).map((x) => x.day);
                            return (
                              <div key={m} className="calendario-mes">
                                <h4 className="cal-mes-title">{months[m - 1]}</h4>
                                <div className="cal-mes-dias">
                                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
                                    const isSel = selDays.indexOf(d) !== -1;
                                    const isInv = invDays.indexOf(d) !== -1;
                                    const cls = 'cal-dia' + (isSel ? ' cal-seleccion' : '') + (isInv ? ' cal-inversion' : '');
                                    return <span key={d} className={cls} title={`${d} ${months[m - 1]}`}>{d}</span>;
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  {c.anexo_ii && <div className="anexo-body" style={{ marginTop: '1rem' }}>{c.anexo_ii}</div>}
                </div>
              )}

              {(c.anexo_iii_equipo || c.equipo) && (c.anexo_iii_equipo?.length > 0 || c.equipo?.length > 0) && (
                <div className="anexo">
                  <h3 className="anexo-title">Anexo III: Equipo Coordinador Pinama Capital 23</h3>
                  <div className="equipo-grid">
                    {(c.anexo_iii_equipo || c.equipo).map((m, i) => (
                      <div key={i} className="equipo-card">
                        <div className="equipo-card-photo">
                          {m.imagen && <img src={m.imagen} alt="" />}
                        </div>
                        <strong>{m.nombre}</strong>
                        <span>{m.rol}</span>
                        {m.descripcion && <span style={{ fontSize: '0.85rem' }}>{m.descripcion}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      ) : (
        /* Vista previa (modal) — estilos anteriores */
        <div className="max-w-4xl mx-auto px-6 py-10 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 shadow-lg my-6 rounded-lg">
          <p className="text-sm text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1">Informe Trimestral</p>
          <h1 className="text-3xl font-bold mb-1">Actividad y Participadas{trimestre} {anio}</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-10">Madrid, {new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          {c.introduccion && (
            <section id={ids.introduccion} className="mb-10 scroll-mt-6">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2">Introducción</h2>
              <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-line">{c.introduccion}</div>
            </section>
          )}
          {c.resumen_ejecutivo && (
            <section id={ids.resumen_ejecutivo} className="mb-10 scroll-mt-6">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2">Resumen Ejecutivo</h2>
              <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-line">{c.resumen_ejecutivo}</div>
            </section>
          )}
          {c.comites && c.comites.length > 0 && (
            <section id={ids.actividad_inversion} className="mb-10 scroll-mt-6">
              <h2 className="text-xl font-semibold mb-2">Actividad de análisis e inversión</h2>
              <h3 className="text-lg font-medium mb-4">Comités</h3>
              <div className="space-y-4">
                {c.comites.map((comite, i) => (
                  <div key={i} className="border rounded-lg p-4">
                    <p className="font-medium text-sm text-zinc-500 dark:text-zinc-400 uppercase">{comite.tipo}</p>
                    <p className="font-semibold">{comite.nombre}</p>
                    <p className="text-sm text-zinc-600 dark:text-zinc-300">{comite.fecha}</p>
                    <p className="text-sm mt-1">{comite.descripcion}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
          {c.eventos && c.eventos.length > 0 && (
            <section id={ids.eventos} className="mb-10 scroll-mt-6">
              <h2 className="text-xl font-semibold mb-4">Presencia en eventos del ecosistema</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {c.eventos.map((ev, i) => (
                  <div key={i} className="border rounded-lg p-4 flex gap-3">
                    {ev.imagen && <img src={ev.imagen} alt="" className="w-24 h-24 object-cover rounded flex-shrink-0" />}
                    <div>
                      <p className="font-medium">{ev.nombre}</p>
                      <p className="text-sm text-zinc-500">{ev.fecha}</p>
                      <p className="text-sm mt-1">{ev.descripcion}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {c.indicadores && Object.keys(c.indicadores).length > 0 && (
            <section id={ids.indicadores} className="mb-10 scroll-mt-6">
              <h2 className="text-xl font-semibold mb-4">Principales hitos e indicadores – {trimestre} {anio}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {Object.entries(c.indicadores).map(([key, value]) => {
                  if (value === '' || value === null || value === undefined) return null;
                  return (
                    <div key={key} className="bg-zinc-50 dark:bg-zinc-800 rounded-lg p-3 text-center">
                      <p className="text-lg font-semibold">{String(value)}</p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{INDICADORES_LABELS[key] || key}</p>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
          {startupsReporte && startupsReporte.length > 0 && (
            <section id={ids.participadas} className="mb-10 scroll-mt-6">
              <h2 className="text-xl font-semibold mb-4">Participadas</h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">Detalle de cada participada con KPIs, valoración y hitos del trimestre.</p>
              <div className="space-y-3">
                {startupsReporte.map((s) => (
                  <div key={s.id} className="border rounded-lg p-4">
                    <p className="font-semibold">{s.nombre}</p>
                    <p className="text-sm text-zinc-500">Fundador: {s.fundador} · Arcángel: {s.arcangel}</p>
                    {s.datos && (s.datos.kpis || s.datos.milestones?.length) && (
                      <div className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
                        {s.datos.kpis && Object.entries(s.datos.kpis).filter(([, v]) => v != null && v !== '').length > 0 && <p>KPIs: {JSON.stringify(s.datos.kpis)}</p>}
                        {s.datos.milestones?.length > 0 && <p>Hitos: {s.datos.milestones.length} hito(s)</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
          {c.calendario && c.calendario.length > 0 && (
            <section id={ids.calendario} className="mb-10 scroll-mt-6">
              <h2 className="text-xl font-semibold mb-4">Eventos programados en el Q4 – {anio}</h2>
              <ul className="list-disc list-inside space-y-2">
                {c.calendario.map((item, i) => (
                  <li key={i}><span className="font-medium">{item.titulo}</span>: {item.fecha}. {item.descripcion}</li>
                ))}
              </ul>
            </section>
          )}
          <section id={ids.anexos} className="mb-10 scroll-mt-6">
            <h2 className="text-xl font-semibold mb-6 border-b pb-2">Anexos</h2>
            {c.anexo_i && <div className="mb-8"><h3 className="text-lg font-medium mb-3">Anexo I: Resumen del vehículo</h3><div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-line">{c.anexo_i}</div></div>}
            {c.anexo_ii && <div className="mb-8"><h3 className="text-lg font-medium mb-3">Anexo II: Calendario de Comités {anio}</h3><div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-line">{c.anexo_ii}</div></div>}
            {(c.anexo_iii_equipo || c.equipo) && (c.anexo_iii_equipo?.length > 0 || c.equipo?.length > 0) && (
              <div>
                <h3 className="text-lg font-medium mb-4">Anexo III: Equipo Coordinador Pinama Capital 23</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  {(c.anexo_iii_equipo || c.equipo).map((m, i) => (
                    <div key={i} className="flex gap-3 border rounded-lg p-4">
                      {m.imagen && <img src={m.imagen} alt="" className="w-16 h-16 object-cover rounded flex-shrink-0" />}
                      <div>
                        <p className="font-semibold">{m.nombre}</p>
                        <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">{m.rol}</p>
                        <p className="text-sm text-zinc-600 dark:text-zinc-300">{m.descripcion}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
